import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth
export const login = (credentials) => api.post('/auth/login', credentials);
export const logout = () => api.post('/auth/logout');
export const checkAuthStatus = () => api.get('/auth/status');

// Apps
export const getApps = () => api.get('/apps');
export const createApp = (data) => api.post('/apps', data);
export const getAppDetails = (appId) => api.get(`/apps/${appId}`);
export const getAppInfo = (appId) => api.get(`/apps/${appId}/info`);

// Versions
export const getAppStoreVersions = (appId, platform = 'IOS') => 
  api.get(`/apps/${appId}/versions`, { params: { platform } });
export const createAppStoreVersion = (appId, data) => 
  api.post(`/apps/${appId}/versions`, data);
export const updateAppStoreVersion = (versionId, data) => 
  api.patch(`/versions/${versionId}`, data);
export const getVersionDetails = (versionId) => 
  api.get(`/versions/${versionId}`);

// Localizations
export const getVersionLocalizations = (versionId) => 
  api.get(`/versions/${versionId}/localizations`);
export const createVersionLocalization = (versionId, locale) => 
  api.post(`/versions/${versionId}/localizations`, { locale });
export const updateVersionLocalization = (localizationId, data) => 
  api.patch(`/localizations/${localizationId}`, data);
export const deleteVersionLocalization = (localizationId) => 
  api.delete(`/localizations/${localizationId}`);
export const getAppInfoLocalizations = (appInfoId) => 
  api.get(`/appinfo/${appInfoId}/localizations`);
export const createAppInfoLocalization = (appInfoId, locale) => 
  api.post(`/appinfo/${appInfoId}/localizations`, { locale });
export const updateAppInfoLocalization = (localizationId, data) => 
  api.patch(`/appinfo/localizations/${localizationId}`, data);
export const deleteAppInfoLocalization = (localizationId) => 
  api.delete(`/appinfo/localizations/${localizationId}`);

// Builds
export const getBuilds = (appId) => api.get(`/apps/${appId}/builds`);
export const updateBuildEncryption = (buildId, usesNonExemptEncryption) => 
  api.patch(`/builds/${buildId}/encryption`, { usesNonExemptEncryption });

// Categories
export const getCategories = () => api.get('/categories');
export const updateAppCategories = (appInfoId, primaryCategoryId, secondaryCategoryId) => 
  api.patch(`/appinfo/${appInfoId}/categories`, { primaryCategoryId, secondaryCategoryId });

// Age Rating
export const getAgeRating = (versionId) => api.get(`/versions/${versionId}/age-rating`);
export const updateAgeRating = (ageRatingId, ratings) => 
  api.patch(`/age-rating/${ageRatingId}`, ratings);
export const updateAgeRatingByVersion = (versionId, ratings) => 
  api.patch(`/versions/${versionId}/age-rating`, ratings);

// Review Details
export const getReviewDetail = (versionId) => api.get(`/versions/${versionId}/review-detail`);
export const createReviewDetail = (versionId, data) => api.post(`/versions/${versionId}/review-detail`, data);
export const updateReviewDetail = (reviewDetailId, data) => 
  api.patch(`/review-detail/${reviewDetailId}`, data);

// Get app review submission (includes rejection reasons)
export const getAppReviewSubmission = (versionId) => 
  api.get(`/versions/${versionId}/review-submission`);

// Screenshots
export const getScreenshots = (localizationId) => 
  api.get(`/localizations/${localizationId}/screenshots`);
export const createScreenshotSet = (localizationId, screenshotDisplayType) => 
  api.post(`/localizations/${localizationId}/screenshot-sets`, { screenshotDisplayType });
export const uploadScreenshot = (screenshotSetId, file) => {
  const formData = new FormData();
  formData.append('screenshot', file);
  return api.post(`/screenshot-sets/${screenshotSetId}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
export const commitScreenshot = (screenshotId) => 
  api.post(`/screenshots/${screenshotId}/commit`);
export const deleteScreenshot = (screenshotId) => 
  api.delete(`/screenshots/${screenshotId}`);
export const reorderScreenshots = (screenshotSetId, screenshotIds) =>
  api.post(`/screenshot-sets/${screenshotSetId}/reorder`, { screenshotIds });
export const deleteScreenshotSet = (screenshotSetId) =>
  api.delete(`/screenshot-sets/${screenshotSetId}`);

// Submit
export const submitForReview = (versionId) => api.post(`/versions/${versionId}/submit`);

// Cancel submission
export const cancelSubmission = (versionId) => api.post(`/versions/${versionId}/cancel-submission`);
export const cancelAppSubmission = (appId) => api.post(`/apps/${appId}/cancel-submission`);

// Developer endpoints
export const getDevices = () => api.get('/devices');
export const getCertificates = () => api.get('/certificates');
export const getProfiles = () => api.get('/profiles');
export const getBundles = () => api.get('/bundles');
export const createBundle = (data) => api.post('/bundles', data);

// Monetization: IAPs & Subscriptions
export const listIAPs = (appId) => api.get(`/apps/${appId}/iaps`);
export const createIAP = (appId, data) => api.post(`/apps/${appId}/iaps`, data);
export const updateIAP = (iapId, data) => api.patch(`/iaps/${iapId}`, data);
export const deleteIAP = (iapId) => api.delete(`/iaps/${iapId}`);
export const listIAPPricePoints = (iapId) => api.get(`/iaps/${iapId}/price-points`);
export const listSubscriptionGroups = (appId) => api.get(`/apps/${appId}/subscription-groups`);
export const createSubscriptionGroup = (appId, data) => api.post(`/apps/${appId}/subscription-groups`, data);
export const listSubscriptionsByGroup = (groupId) => api.get(`/subscription-groups/${groupId}/subscriptions`);
export const createSubscription = (groupId, data) => api.post(`/subscription-groups/${groupId}/subscriptions`, data);
export const listTerritories = () => api.get('/territories');
export const listSubscriptionPricePoints = (subscriptionId, territory = 'USA') =>
  api.get(`/subscriptions/${subscriptionId}/price-points`, { params: { territory } });
export const listAllSubscriptionPricePoints = (subscriptionId) =>
  api.get(`/subscriptions/${subscriptionId}/all-price-points`);
export const setSubscriptionUsdPrice = (subscriptionId, usdPrice, startDate, territories) =>
  api.post(`/subscriptions/${subscriptionId}/price-usd`, { usdPrice, startDate, territories });

export const createSubscriptionLocalization = (subscriptionId, data) =>
  api.post(`/subscriptions/${subscriptionId}/localizations`, data);

export const getSubscriptionReviewScreenshots = (subscriptionId) =>
  api.get(`/subscriptions/${subscriptionId}/review-screenshots`);

export const createSubscriptionReviewScreenshot = (subscriptionId, data) =>
  api.post(`/subscriptions/${subscriptionId}/review-screenshots`, data);

// Pricing and Availability
export const getPricingAndAvailability = (appId) => api.get(`/apps/${appId}/pricing-availability`);
export const updatePricingAndAvailability = (appId, data) => api.patch(`/apps/${appId}/pricing-availability`, data);
export const updateAppPricing = (appId, data) => api.patch(`/apps/${appId}/pricing`, data);
export const updateAppAvailability = (appId, data) => api.patch(`/apps/${appId}/availability`, data);
export const getTerritories = () => api.get('/territories');
export const getAppPrices = (appId) => api.get(`/apps/${appId}/prices`);
export const updateAppPrices = (appId, data) => api.patch(`/apps/${appId}/prices`, data);

// Data Usage / Privacy Declarations
export const getAppDataUsage = (appId) => api.get(`/apps/${appId}/data-usage`);
export const createAppDataUsageDeclaration = (appId, data) => api.post(`/apps/${appId}/data-usage`, data);
export const createNoDataCollectedDeclaration = (appId) => api.post(`/apps/${appId}/data-usage/no-data`, {});
export const createBasicDataUsageDeclarations = (appId, appType) => api.post(`/apps/${appId}/data-usage/basic`, { appType });
export const updateAppPrivacyPolicy = (appId, privacyPolicyUrl) => api.patch(`/apps/${appId}/privacy-policy`, { privacyPolicyUrl });

// Content Rights
export const getContentRights = (appId) => api.get(`/apps/${appId}/content-rights`);
export const updateContentRights = (appId, declaration) => api.patch(`/apps/${appId}/content-rights`, { declaration });

export default api;
